import { useEffect, useMemo, useState, useCallback } from 'react';
import './CharacterShapingProgram.css';

import treeImage from './assets/Tree_for_Character_Shaping_Program.png';

import heroSlide1 from './assets/SLHero_FirstDay.png';

import heroSlide2 from './assets/SLHero_IMG3999.jpg';

import heroSlide3 from './assets/SLHero_StudentsLife.jpg';

import clubECASport from './assets/ClubECA_Sport_1.jpeg';
import clubECAArt from './assets/ClubECA_Arts_1.jpg';
import clubECALeadership from './assets/ClubECA_Leadership_1.jpg';
import clubECALanguage from './assets/ClubECA_Language_1.JPG';
import clubECASport2 from './assets/ClubECA_Sport_2.png';
import clubECAArt2 from './assets/ClubECA_Arts_2.png';
import clubECALeadership2 from './assets/ClubECA_Leadership_2.jpg';
import clubECALanguage2 from './assets/ClubECA_Language_2.jpg';

import eventSpartan1 from './assets/EventComp_Spartan_1.jpg';
import eventSpartan2 from './assets/EventComp_Spartan_2.JPG';
import eventEntrepreneur1 from './assets/EventComp_Enterprenuer_1.jpeg';
import eventEntrepreneur2 from './assets/EventComp_Enterprenuer_2.jpeg';
import eventInnovation1 from './assets/EventComp_Innovation_1.png';

import houseSystemCourage from './assets/HouseSystem_Courage.png';
import houseSystemFaith from './assets/HouseSystem_Faith.png';
import houseSystemHope from './assets/HouseSystem_Hope.png';
import houseSystemLove from './assets/HouseSystem_Love.png';
import houseSystemPeace from './assets/HouseSystem_Peace.png';

import wellbeingSEL1 from './assets/Wellbeing_SEL_1.jpg';
import wellbeingSEL2 from './assets/Wellbeing_SEL_2.jpg';
import wellbeingSEL3 from './assets/Wellbeing_SEL_3.jpg';

const API_BASE = process.env.REACT_APP_API_BASE || 'https://permataharapansecondary.sch.id/api';

function StudentLifePage({ page, onNavigate }) {

  const defaultHeroSlides = [heroSlide1, heroSlide2, heroSlide3];
  const [heroSlides, setHeroSlides] = useState(defaultHeroSlides);

  const [cmsData, setCmsData] = useState(null);
  const [mediaMap, setMediaMap] = useState({});
  const [dataLoaded, setDataLoaded] = useState(false);
  const [mediaLoaded, setMediaLoaded] = useState(false);

  const [activeHero, setActiveHero] = useState(0);

  const [activeWellbeingSlide, setActiveWellbeingSlide] = useState(0);

  const [activeWellbeingCard, setActiveWellbeingCard] = useState(0);

  const [activeTreeZone, setActiveTreeZone] = useState('easternCulture');
  const [showTreeModal, setShowTreeModal] = useState(false);
  const [flippedLeapsCard, setFlippedLeapsCard] = useState(null);



  // Fetch media map
  const fetchMediaMap = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/studentlife/cms/media?_t=${Date.now()}`, {
        credentials: 'include',
        headers: { 'Cache-Control': 'no-cache' }
      });
      if (!response.ok) {
        setMediaLoaded(true);
        return;
      }
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        const map = {};
        result.data.forEach(m => {
          const id = m.id || m.media_id;
          const url = m.url || m.file_path;
          if (id && url) map[id] = map[String(id)] = url;
        });
        setMediaMap(map);
      }
    } catch (err) {
      console.error('[StudentLifePage] Media fetch error:', err.message);
    } finally {
      setMediaLoaded(true);
    }
  }, []);

  // Fetch CMS sections
  const fetchCMSSections = useCallback(async () => {
    try {
      const timestamp = Date.now();
      const url = `${API_BASE}/studentlife/cms/sections?published=true&_t=${timestamp}&_=${Math.random()}`;
      
      console.log('[StudentLifePage] Fetching CMS from:', url);
      
      const response = await fetch(url, {
        credentials: 'include',
        headers: { 
          'Content-Type': 'application/json', 
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      if (!response.ok) {
        console.error('[StudentLifePage] CMS fetch failed, status:', response.status);
        setDataLoaded(true);
        return;
      }
      const result = await response.json();
      if (result.ok && Array.isArray(result.data)) {
        console.log('[StudentLifePage] CMS data received, sections:', result.data.map(s => s.section_type));
        setCmsData(result.data);
      }
    } catch (err) {
      console.error('[StudentLifePage] CMS fetch error:', err);
    } finally {
      setDataLoaded(true);
    }
  }, []);

  // Fetch on mount and every 5 seconds for live updates (aggressive polling for approval changes)
  useEffect(() => {
    fetchMediaMap();
    fetchCMSSections();

    // Poll for updates every 5 seconds (very frequent) to refresh approved changes immediately
    const intervalId = setInterval(() => {
      console.log('[StudentLifePage] Polling for updates...');
      fetchMediaMap();
      fetchCMSSections();
    }, 5000);

    // Refresh when tab becomes visible (user returns to tab) - FORCED refresh
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[StudentLifePage] Tab became visible - force refreshing data');
        fetchMediaMap();
        fetchCMSSections();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchMediaMap, fetchCMSSections]);

  // Extract hero section from CMS
  const heroSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return {};
    const section = cmsData.find(s => s.section_type === 'student-life-hero');
    if (!section) return {};
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      return enData;
    } catch (e) {
      console.error('[StudentLifePage] Failed to parse hero:', e);
      return {};
    }
  }, [cmsData]);

  // Update hero slides from CMS data with media resolution
  useEffect(() => {
    if (heroSection?.slides && heroSection.slides.length > 0) {
      const resolvedSlides = heroSection.slides.map(slide => {
        if (typeof slide === 'object' && slide.image) {
          return mediaMap[slide.image] || defaultHeroSlides[0];
        }
        return mediaMap[slide] || defaultHeroSlides[0];
      });
      setHeroSlides(resolvedSlides);
    }
  }, [heroSection, mediaMap]);

  useEffect(() => {

    const intervalId = setInterval(() => {

      setActiveHero((current) => (current + 1) % heroSlides.length);

    }, 6000);



    return () => clearInterval(intervalId);

  }, [heroSlides.length]);



  useEffect(() => {

    const intervalId = setInterval(() => {

      setActiveWellbeingSlide((current) => (current + 1) % 3);

    }, 5000);



    return () => clearInterval(intervalId);

  }, []);



  // Extract clubs section from CMS
  const clubsSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) {
      console.log('[StudentLifePage] No CMS data available');
      return null;
    }
    const section = cmsData.find(s => s.section_type === 'student-life-clubs');
    if (!section) {
      console.error('[StudentLifePage] ❌ Clubs section not found in CMS. Available sections:', cmsData.map(s => s.section_type));
      return null;
    }
    
    console.log('[StudentLifePage] 🔍 Clubs section raw content_published type:', typeof section.content_published);
    console.log('[StudentLifePage] 🔍 Clubs section raw content_published (first 500 chars):', JSON.stringify(section.content_published).substring(0, 500));
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') {
        published = JSON.parse(published);
      }
      const enData = published?.en || {};
      console.log('[StudentLifePage] ✓ Clubs section parsed successfully');
      console.log('[StudentLifePage]   - enData keys:', Object.keys(enData));
      console.log('[StudentLifePage]   - Clubs array exists:', !!enData.clubs);
      console.log('[StudentLifePage]   - Clubs is array:', Array.isArray(enData.clubs));
      console.log('[StudentLifePage]   - Clubs count:', enData.clubs?.length || 0);
      if (enData.clubs && enData.clubs.length > 0) {
        console.log('[StudentLifePage]   - First club title:', enData.clubs[0]?.title);
        console.log('[StudentLifePage]   - First club complete:', JSON.stringify(enData.clubs[0]).substring(0, 200));
      }
      return enData;
    } catch (e) {
      console.error('[StudentLifePage] ❌ Failed to parse clubs:', e.message);
      console.error('[StudentLifePage] Raw content_published:', section.content_published);
      return null;
    }
  }, [cmsData]);

  // Build clubs array from CMS with media resolution
  const clubs = useMemo(
    () => {
      console.log('[StudentLifePage] clubs useMemo called, clubsSection:', clubsSection);
      
      if (clubsSection?.clubs && Array.isArray(clubsSection.clubs)) {
        console.log('[StudentLifePage] Using CMS clubs, count:', clubsSection.clubs.length);
        return clubsSection.clubs.map(club => ({
          ...club,
          images: Array.isArray(club.images) 
            ? club.images.map(imgId => mediaMap[imgId] || clubECASport)
            : [clubECASport]
        }));
      }
      
      // Fallback to local defaults if no CMS data
      console.log('[StudentLifePage] clubsSection?.clubs check failed:');
      console.log('  clubsSection:', clubsSection);
      console.log('  clubsSection?.clubs:', clubsSection?.clubs);
      console.log('  Array.isArray:', Array.isArray(clubsSection?.clubs));
      console.log('  Using fallback with 4 clubs');
      
      return [
        {
          title: 'Sports & Athletics Club',
          subtitle: 'Teamwork, discipline, and healthy competition',
          description:
            'Students build confidence through structured training sessions, friendly matches, and seasonal tournaments. Activities include futsal, basketball, badminton, and athletics conditioning tailored to each level.',
          highlights: ['Weekly training sessions', 'Interschool friendly matches', 'Fitness and agility drills'],
          images: [clubECASport, clubECASport2],
        },
        {
          title: 'Arts, Music, and Performance',
          subtitle: 'Creativity on stage and beyond',
          description:
            'From choir to contemporary dance, students explore creative expression with guidance from professional coaches. Showcases, exhibitions, and recitals help students share their talents with the community.',
          highlights: ['Choir and ensemble practice', 'Dance and theater workshops', 'Seasonal showcases'],
          images: [clubECAArt, clubECAArt2],
        },
        {
          title: 'Leadership & Community Service',
          subtitle: 'Grow character through impact',
          description:
            'Student leaders plan campus initiatives, service programs, and charity drives. Activities are designed to build empathy, communication skills, and a spirit of collaboration.',
          highlights: ['Student council programs', 'Community outreach', 'Leadership retreats'],
          images: [clubECALeadership, clubECALeadership2],
        },
        {
          title: 'Language and Cultural Club',
          subtitle: 'Celebrate global perspectives',
          description:
            'Language immersion sessions, cultural days, and exchange projects build confidence and appreciation for diversity. Students practice real-world communication through themed activities.',
          highlights: ['Cultural exchange days', 'Language immersion sessions', 'International celebration week'],
          images: [clubECALanguage, clubECALanguage2],
        },
      ];
    },
    [clubsSection, mediaMap]
  );



  const [activeClubSlides, setActiveClubSlides] = useState(

    clubs.map(() => 0)

  );



  // Extract events section from CMS
  const eventsSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) {
      console.warn('[StudentLifePage] No CMS data for events');
      return null;
    }
    const section = cmsData.find(s => s.section_type === 'student-life-events');
    if (!section) {
      console.error('[StudentLifePage] ❌ Events section NOT found. Available sections:', cmsData.map(s => s.section_type));
      return null;
    }
    
    console.log('[StudentLifePage] ✓ Events section found, content_published keys:', Object.keys(section.content_published || {}));
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      console.log('[StudentLifePage] Events section parsed - kicker:', enData.kicker, 'events count:', enData.events?.length || 0);
      return enData;
    } catch (e) {
      console.error('[StudentLifePage] Failed to parse events:', e);
      return null;
    }
  }, [cmsData]);

  // Build events array from CMS with media resolution
  const events = useMemo(
    () => {
      if (eventsSection?.events && Array.isArray(eventsSection.events)) {
        return eventsSection.events.map(event => ({
          ...event,
          images: Array.isArray(event.images)
            ? event.images.map(imgId => mediaMap[imgId] || eventSpartan1)
            : [eventSpartan1]
        }));
      }

      // Fallback to local defaults
      return [
        {
          title: 'SPARTAN CUPS',
          subtitle: 'Annual sports, arts, and leadership competitions',
          description:
            'Spartan Cups brings together students across levels for a full week of friendly competition, teamwork, and school pride. The program includes house teams, skill clinics, and a final celebration night with awards.',
          highlights: ['House leagues and leadership roles', 'Skill clinics led by coaches', 'Championship night and awards'],
          schedule: [
            'Week 1: Team drafting and leadership workshop',
            'Week 2: Qualifiers and sports clinics',
            'Week 3: Creative showcase + finals',
          ],
          images: [eventSpartan1, eventSpartan2],
        },
        {
          title: 'Entrepreneurship Program',
          subtitle: 'Ideas to impact: student-led enterprise journey',
          description:
            'Students learn how to build a business idea, pitch with confidence, and manage a mini enterprise. The program blends mentorship, budgeting, and storytelling so students gain real-world financial literacy and teamwork skills.',
          highlights: ['Mentor-led ideation labs', 'Market day with student booths', 'Pitching and storytelling training'],
          schedule: [
            'Phase 1: Idea sprint + customer interviews',
            'Phase 2: Budgeting and product testing',
            'Phase 3: Market day + final pitch',
          ],
          images: [eventEntrepreneur1, eventEntrepreneur2],
        },
        {
          title: 'Innovation, Coding, and Scientific Discovery',
          subtitle: 'STEM Program',
          description:
            'From coding challenges to science fairs, the STEM program encourages experimentation and collaboration. Students work on real problems, build prototypes, and present solutions at the annual Innovation Expo.',
          highlights: ['Robotics and coding labs', 'Science fair exhibition', 'Innovation Expo and demos'],
          schedule: [
            'Cycle 1: Design thinking and research',
            'Cycle 2: Build, test, and iterate',
            'Cycle 3: Expo prep and showcases',
          ],
          images: [eventInnovation1],
        },
      ];
    },
    [eventsSection, mediaMap]
  );



  // Extract house system section from CMS
  const houseSystemSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return null;
    const section = cmsData.find(s => s.section_type === 'student-life-house-system');
    if (!section) {
      console.warn('[StudentLifePage] No house system section found in CMS data. Available sections:', cmsData.map(s => s.section_type));
      return null;
    }
    
    console.log('[StudentLifePage] House system section found:', section);
    
    try {
      let published = section.content_published;
      console.log('[StudentLifePage] Raw content_published:', published);
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      console.log('[StudentLifePage] Parsed house system enData:', enData);
      return enData;
    } catch (e) {
      console.error('[StudentLifePage] Failed to parse house system:', e);
      return null;
    }
  }, [cmsData]);

  // Build houseSystem object from CMS with media resolution
  const houseSystem = useMemo(
    () => {
      if (houseSystemSection?.houses && Array.isArray(houseSystemSection.houses)) {
        console.log('[StudentLifePage] Building house system from CMS:');
        console.log('[StudentLifePage] - Houses count:', houseSystemSection.houses.length);
        console.log('[StudentLifePage] - Media map size:', Object.keys(mediaMap).length);
        
        const resolvedHouses = houseSystemSection.houses.map((house, idx) => {
          const imageId = house.image;
          const resolvedImage = mediaMap[imageId] || houseSystemCourage;
          console.log(`[StudentLifePage] - House ${idx} (${house.name}):`, {
            imageId,
            resolvedImage: resolvedImage.substring(0, 60),
            inMediaMap: !!mediaMap[imageId]
          });
          
          return {
            ...house,
            image: resolvedImage
          };
        });
        
        return {
          ...houseSystemSection,
          houses: resolvedHouses
        };
      }

      // Fallback to local defaults
      console.log('[StudentLifePage] Using fallback house data (no CMS data)');
      return {
        title: 'House System',
        kicker: 'House Systems',
        subtitle: 'Belonging, leadership, and school pride',
        description:
          'Our House System groups students across grades into supportive teams that build unity, responsibility, and healthy competition. Each house earns points through academics, service, and events, encouraging students to lead with character.',
        houses: [
          { name: 'House of Courage', motto: 'Courage Unlocks Dreams', color: '#dc2626', image: houseSystemCourage },
          { name: 'House of Love', motto: 'Love Guides Us Forward', color: '#ec4899', image: houseSystemLove },
          { name: 'House of Hope', motto: 'Hope Lights the Way', color: '#eab308', image: houseSystemHope },
          { name: 'House of Faith', motto: 'Faith Guides Our Path', color: '#f97316', image: houseSystemFaith },
          { name: 'House of Peace', motto: 'Peace Leads Us Forward', color: '#22c55e', image: houseSystemPeace },
        ],
        image: houseSystemCourage,
        imageLabel: 'House Gallery',
        imageCaption: 'Space for a featured house photo or house activity moment.',
      };
    },
    [houseSystemSection, mediaMap]
  );



  // Extract wellbeing section from CMS
  const wellbeingSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) return null;
    const section = cmsData.find(s => s.section_type === 'student-life-wellbeing');
    if (!section) return null;
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') published = JSON.parse(published);
      const enData = published?.en || {};
      return enData;
    } catch (e) {
      console.error('[StudentLifePage] Failed to parse wellbeing:', e);
      return null;
    }
  }, [cmsData]);

  // Build wellbeing object from CMS with media resolution
  const wellbeing = useMemo(
    () => {
      if (wellbeingSection) {
        return {
          ...wellbeingSection,
          images: Array.isArray(wellbeingSection.images)
            ? wellbeingSection.images.map(imgId => mediaMap[imgId] || wellbeingSEL1)
            : [wellbeingSEL1, wellbeingSEL2, wellbeingSEL3]
        };
      }

      // Fallback to local defaults
      return {
        title: 'Wellbeing & Social Emotional Learning',
        subtitle: 'Support for emotional and academic growth',
        description:
          'Our SEL framework helps students build resilience, confidence, and healthy relationships while supporting academic success. Dedicated counselors, advisory teachers, and structured lessons ensure every student feels seen and supported.',
        pillars: ['Self-awareness', 'Emotional regulation', 'Empathy & kindness', 'Goal setting', 'Positive relationships'],
        supports: [
          {
            title: 'Counselor Check-ins',
            detail: 'Regular sessions for guidance, emotional support, and personal goal planning.',
            icon: 'fa-user-tie',
          },
          {
            title: 'Advisory Circles',
            detail: 'Weekly small-group conversations that build trust and communication skills.',
            icon: 'fa-users',
          },
          {
            title: 'Academic Coaching',
            detail: 'Study strategies, time management, and learning support tailored to each student.',
            icon: 'fa-book',
          },
          {
            title: 'Family Partnership',
            detail: 'Parent updates, workshops, and shared action plans for wellbeing at home.',
            icon: 'fa-home',
          },
          {
            title: 'Peer Mentoring',
            detail: 'Older students support younger peers through buddy programs, fostering leadership and belonging.',
            icon: 'fa-handshake',
          },
        ],
        images: [wellbeingSEL1, wellbeingSEL2, wellbeingSEL3],
      };
    },
    [wellbeingSection, mediaMap]
  );



  // Extract character-shaping section from CMS
  const characterShapingSection = useMemo(() => {
    if (!cmsData || cmsData.length === 0) {
      console.warn('[StudentLifePage] cmsData is empty or null');
      return null;
    }
    
    const section = cmsData.find(s => s.section_type === 'student-life-character-shaping');
    if (!section) {
      console.warn('[StudentLifePage] Character shaping section not found in CMS. Available sections:', cmsData.map(s => s.section_type));
      return null;
    }
    
    console.log('[StudentLifePage] Found character-shaping section:', section.id, 'status:', section.status);
    
    try {
      let published = section.content_published;
      if (typeof published === 'string') {
        console.log('[StudentLifePage] Parsing JSON content_published');
        published = JSON.parse(published);
      }
      
      const enData = published?.en || {};
      console.log('[StudentLifePage] Extracted enData:', {
        hasKicker: !!enData.kicker,
        hasTitle: !!enData.title,
        hasZones: !!enData.zones,
        zones: enData.zones ? Object.keys(enData.zones) : 'none'
      });
      
      return enData;
    } catch (e) {
      console.error('[StudentLifePage] Failed to parse character-shaping:', e);
      return null;
    }
  }, [cmsData]);

  // Build treeZoneContent from CMS with fallback to hardcoded defaults
  const treeZoneContent = useMemo(() => {
    if (characterShapingSection?.zones) {
      console.log('[StudentLifePage] Using character-shaping zones from CMS');
      const zones = characterShapingSection.zones;
      return {
        wellbeing: {
          title: zones.wellbeing?.title || 'WELLBEING',
          subtitle: zones.wellbeing?.subtitle || 'Support for emotional and academic growth',
          content: zones.wellbeing?.pillars || []
        },
        sel: {
          title: zones.sel?.title || 'SOCIAL EMOTIONAL LEARNING (SEL) as STEM',
          subtitle: zones.sel?.subtitle || 'Essential soft skills development based on eastern culture value',
          content: zones.sel?.pillars || []
        },
        easternCulture: {
          title: zones.easternCulture?.title || 'EASTERN CULTURE VALUE as the ROOT',
          subtitle: zones.easternCulture?.subtitle || 'Menjadi fondasi karakter dengan pilar',
          content: zones.easternCulture?.pillars || []
        }
      };
    }

    // Fallback to hardcoded defaults
    console.warn('[StudentLifePage] characterShapingSection is missing zones, using hardcoded fallback. CharacterShapingSection:', characterShapingSection);
    return {
      wellbeing: {
        title: 'WELLBEING',
        subtitle: 'Support for emotional and academic growth',
        content: [
          {
            heading: 'Physical Wellbeing (Kesehatan fisik)',
            points: [
              'Cognitive stamina and focus',
              'Emotional regulation & alertness',
              'Psychomotor development',
              'Vitality, resilience, stress management'
            ]
          },
          {
            heading: 'Social Wellbeing (Kesehatan sosial)',
            points: [
              'Active listening',
              'Conflict resolution',
              'Inclusivity',
              'Responsible and engaged'
            ]
          },
          {
            heading: 'Emotional wellbeing (kesehatan emosional)',
            points: [
              'Metacognitive awareness'
            ]
          }
        ]
      },
      sel: {
        title: 'SOCIAL EMOTIONAL LEARNING (SEL) as STEM',
        subtitle: 'Essential soft skills development based on eastern culture value',
        content: [
          {
            heading: 'SELF – AWARENESS',
            points: [
              'Recognizing own emotions, thought and values',
              'Understand how feelings influence behaviour',
              'Well-grounded sense of growth mindset'
            ]
          },
          {
            heading: 'SELF – MANAGEMENT',
            points: [
              'Managing stress, controlling impulses',
              'Staying motivated to reach personal / academic goals'
            ]
          },
          {
            heading: 'SOCIAL AWARENESS',
            points: [
              'Perspective of others, with diverse backgrounds',
              'Understanding social cues and norms'
            ]
          },
          {
            heading: 'RELATIONSHIP SKILLS',
            points: [
              'Establish and maintain healthy relationship',
              'Communicating clearly, listening attentively, cooperating',
              'Resisting social pressure including bullying'
            ]
          },
          {
            heading: 'RESPONSIBLE DECISION – MAKING',
            points: [
              'Making constructive choices',
              'Social interaction based on ethical standards, safety concerns and social norms'
            ]
          }
        ]
      },
      easternCulture: {
        title: 'EASTERN CULTURE VALUE as the ROOT',
        subtitle: 'Menjadi fondasi karakter dengan pilar',
        content: [
          {
            heading: 'GRACE AND COURTESY (RESPECT)',
            points: [
              'Respect the Elders',
              'Respect the Authority and Hierarchy',
              'Respect the Environment',
              'Respect the Traditional & Cultural heritage'
            ]
          },
          {
            heading: 'HARMONY AND BALANCE',
            points: [
              'Kindness, social harmony',
              'Mutual Cooperation (Gotong royong)',
              'Filial piety (Bakti kepada orangtua)'
            ]
          },
          {
            heading: 'SELF-DISCIPLINE',
            points: [
              'Self Commitment & Orderliness',
              'Self-Restraint, Self-Controlled',
              'Perseverance & Grit'
            ]
          },
          {
            heading: 'SPIRITUAL & MORAL',
            points: [
              'Faith & Spirituality',
              'Honesty',
              'Humility & Modesty',
              'Mindfulness'
            ]
          }
        ]
      }
    };
  }, [characterShapingSection]);

  useEffect(() => {
    setActiveClubSlides(clubs.map(() => 0));
  }, [clubs]);



  const [activeEventSlides, setActiveEventSlides] = useState(

    events.map(() => 0)

  );



  useEffect(() => {
    setActiveEventSlides(events.map(() => 0));
  }, [events]);



  useEffect(() => {

    const intervalId = setInterval(() => {

      setActiveClubSlides((current) =>

        current.map((value, index) => (value + 1) % clubs[index].images.length)

      );

    }, 4500);



    return () => clearInterval(intervalId);

  }, [clubs]);



  useEffect(() => {

    const intervalId = setInterval(() => {

      setActiveEventSlides((current) =>

        current.map((value, index) => (value + 1) % events[index].images.length)

      );

    }, 4800);



    return () => clearInterval(intervalId);

  }, [events]);



  useEffect(() => {

    const animatedItems = document.querySelectorAll('.life-animate, .event-animate');

    if (!animatedItems.length) {

      return undefined;

    }



    const observer = new IntersectionObserver(

      (entries) => {

        entries.forEach((entry) => {

          if (entry.isIntersecting) {

            entry.target.classList.add('is-visible');

            observer.unobserve(entry.target);

          }

        });

      },

      { threshold: 0.2 }

    );



    animatedItems.forEach((item) => {
      // Check if element is already in viewport on mount
      const rect = item.getBoundingClientRect();
      const isInViewport = rect.top < window.innerHeight && rect.bottom > 0;
      
      if (isInViewport) {
        item.classList.add('is-visible');
      } else {
        observer.observe(item);
      }
    });



    return () => observer.disconnect();

  }, [clubs, events]);



  const heroTitle = heroSection?.title || 'Student Life';

  const heroSummary = heroSection?.summary || 'A vibrant campus rhythm filled with clubs, leadership, arts, sports, and community experiences.';



  return (

    <section className="student-life-page">

      <section className="student-life-hero" id="student-life-overview">

        <div className="student-life-hero-media" aria-hidden="true">

          {heroSlides.map((slide, index) => (

            <div

              key={slide}

              className={`student-life-hero-slide ${index === activeHero ? 'active' : ''}`}

            >

              <img src={slide} alt="Student life hero" />

            </div>

          ))}

        </div>

        <div className="student-life-hero-overlay" aria-hidden="true" />

        <div className="student-life-hero-content">

          <p className="student-life-hero-kicker">Student Life</p>

          <h2>{heroTitle}</h2>

          <p>{heroSummary}</p>

          <div className="student-life-hero-dots" role="tablist" aria-label="Student life hero slides">

            {heroSlides.map((slide, index) => (

              <button

                key={slide}

                type="button"

                className={`student-life-hero-dot ${index === activeHero ? 'active' : ''}`}

                onClick={() => setActiveHero(index)}

                aria-label={`Show slide ${index + 1}`}

              />

            ))}

          </div>

        </div>

      </section>



      <section className="student-life-clubs" id="clubs-eca">

        <div className="student-life-clubs-header">

          <p className="student-life-clubs-kicker">{clubsSection?.kicker || 'Clubs & ECA'}</p>

          <h3>{clubsSection?.title || 'Explore activities that build confidence, creativity, and friendship'}</h3>

          <p>
            {clubsSection?.description || 'Our clubs and extracurricular activities are designed to help students discover passions, practice leadership, and build lasting skills beyond the classroom. Each club features recurring sessions, guided mentorship, and student-led showcases.'}
          </p>

        </div>



        <div className="student-life-clubs-grid">

          {clubs.map((club, index) => (

            <article className="student-life-club-card life-animate" key={`club-${index}`}>

              <div className="student-life-club-content">

                <p className="student-life-club-label">{club.subtitle}</p>

                <h4>{club.title}</h4>

                <p>{club.description}</p>

                <ul>

                  {club.highlights.map((item) => (

                    <li key={item}>{item}</li>

                  ))}

                </ul>

              </div>



              <div className="student-life-club-media" aria-hidden="true">

                {club.images.map((image, imageIndex) => (

                  <div

                    key={`${club.title}-${image}`}

                    className={`student-life-club-slide ${

                      imageIndex === activeClubSlides[index] ? 'active' : ''

                    }`}

                  >

                    <img src={image} alt={`${club.title} activity`} />

                  </div>

                ))}

                <div className="student-life-club-dots">

                  {club.images.map((image, imageIndex) => (

                    <button

                      key={`${club.title}-${image}-dot`}

                      type="button"

                      className={`student-life-club-dot ${

                        imageIndex === activeClubSlides[index] ? 'active' : ''

                      }`}

                      onClick={() =>

                        setActiveClubSlides((current) =>

                          current.map((value, idx) =>

                            idx === index ? imageIndex : value

                          )

                        )

                      }

                      aria-label={`Show ${club.title} slide ${imageIndex + 1}`}

                    />

                  ))}

                </div>

              </div>

            </article>

          ))}

        </div>

      </section>



      <section className="events-list" id="events-competitions">

        <div className="events-list-header">

          <p className="events-list-kicker">{(() => {
            const val = eventsSection?.kicker || 'Events & Competitions';
            console.log('[StudentLifePage] Rendering events kicker:', val, 'eventsSection:', eventsSection);
            return val;
          })()}</p>

          <h3>{(() => {
            const val = eventsSection?.title || 'SPARTAN Cups, Entrepreneurship, and STEM highlights';
            console.log('[StudentLifePage] Rendering events title:', val);
            return val;
          })()}</h3>

          <p>

            {eventsSection?.description || 'Signature programs that build teamwork, innovation, and leadership. Each event includes mentoring, showcases, and hands-on activities that keep students engaged throughout the year.'}

          </p>

        </div>



        <div className="events-grid">

          {events.map((event, index) => (

            <article className="events-card event-animate" key={event.title}>

              <div className="events-card-content">

                <p className="events-card-label">{event.subtitle}</p>

                <h4>{event.title}</h4>

                <p>{event.description}</p>

                <div className="events-card-section">

                  <h5>Highlights</h5>

                  <div className="events-chip-row">

                    {event.highlights.map((item) => (

                      <span key={item}>{item}</span>

                    ))}

                  </div>

                </div>

                <div className="events-card-section">

                  <h5>Program Flow</h5>

                  <ul>

                    {event.schedule.map((item) => (

                      <li key={item}>{item}</li>

                    ))}

                  </ul>

                </div>

              </div>



              <div className="events-card-media" aria-hidden="true">

                {event.images.map((image, imageIndex) => (

                  <div

                    key={`${event.title}-${image}`}

                    className={`events-card-slide ${

                      imageIndex === activeEventSlides[index] ? 'active' : ''

                    }`}

                  >

                    <img src={image} alt={`${event.title} activity`} />

                  </div>

                ))}

                <div className="events-card-dots">

                  {event.images.map((image, imageIndex) => (

                    <button

                      key={`${event.title}-${image}-dot`}

                      type="button"

                      className={`events-card-dot ${

                        imageIndex === activeEventSlides[index] ? 'active' : ''

                      }`}

                      onClick={() =>

                        setActiveEventSlides((current) =>

                          current.map((value, idx) => (idx === index ? imageIndex : value))

                        )

                      }

                      aria-label={`Show ${event.title} slide ${imageIndex + 1}`}

                    />

                  ))}

                </div>

              </div>

            </article>

          ))}

        </div>

      </section>



      <section className="house-system" id="house-system">

        <div className="house-system-header">

          <p className="house-system-kicker">{houseSystem.kicker || 'House System'}</p>

          <h3>{houseSystem.title}</h3>

          <p>{houseSystem.description}</p>

        </div>



        <div className="house-system-grid">

          {houseSystem.houses.map((house) => (

            <article

              key={house.name}

              className="house-system-card life-animate"

              onClick={() => onNavigate && onNavigate(`house-${house.name.toLowerCase().replace(/\s+/g, '-')}`)}

              role="button"

              tabIndex={0}

              onKeyPress={(e) => {

                if (e.key === 'Enter' || e.key === ' ') {

                  onNavigate && onNavigate(`house-${house.name.toLowerCase().replace(/\s+/g, '-')}`);

                }

              }}

            >

              <div className="house-system-card-image" style={{ backgroundImage: `url(${house.image})` }} aria-hidden="true">

                <div className="house-system-card-overlay" />

              </div>

              <div className="house-system-card-content">

                <h4>{house.name}</h4>

                <p className="house-system-card-motto">"{house.motto}"</p>

                <div className="house-system-card-badge" style={{ backgroundColor: house.color }} />

              </div>

            </article>

          ))}

        </div>

      </section>



      <section className="wellbeing-section" id="wellbeing-sel">

        <div className="wellbeing-container">

          <div className="wellbeing-header">

            <p className="wellbeing-kicker">{wellbeing.kicker || 'Wellbeing & Social Emotional Learning'}</p>

            <h3>{wellbeing.title || 'Support for emotional and academic growth'}</h3>

            <p>{wellbeing.description || 'Our SEL framework helps students build resilience, confidence, and healthy relationships while supporting academic success. Dedicated counselors, advisory teachers, and structured lessons ensure every student feel seen and supported.'}</p>

          </div>



          <div className="wellbeing-content">

            <div className="wellbeing-image-section life-animate">

              <div className="wellbeing-image-wrapper">

                {wellbeing.images.map((image, index) => (

                  <div

                    key={`wellbeing-img-${index}`}

                    className={`wellbeing-image-slide ${index === activeWellbeingSlide ? 'active' : ''}`}

                  >

                    <img src={image} alt="Wellbeing moment" />

                  </div>

                ))}

                <div className="wellbeing-image-dots">

                  {[0, 1, 2].map((index) => (

                    <button

                      key={`wellbeing-img-dot-${index}`}

                      type="button"

                      className={`wellbeing-image-dot ${index === activeWellbeingSlide ? 'active' : ''}`}

                      onClick={() => setActiveWellbeingSlide(index)}

                      aria-label={`Show slide ${index + 1}`}

                    />

                  ))}

                </div>

              </div>

            </div>



            <div className="wellbeing-info-section">

              <div className="wellbeing-pillars-section life-animate">

                <h4>Core Pillars</h4>

                <div className="wellbeing-pillars-grid">

                  {wellbeing.pillars.map((pillar, index) => (

                    <div key={pillar} className="wellbeing-pillar-item">

                      <div className="wellbeing-pillar-icon">

                        <i className="fas fa-star" />

                      </div>

                      <p>{pillar}</p>

                    </div>

                  ))}

                </div>

              </div>



              <div className="wellbeing-supports-section life-animate">

                <h4>Support Pathways</h4>

                <div className="wellbeing-supports-grid">

                  {wellbeing.supports.map((support, index) => (

                    <div

                      key={support.title}

                      className={`wellbeing-support-card ${index === activeWellbeingCard ? 'active' : ''}`}

                      onClick={() => setActiveWellbeingCard(index)}

                      role="button"

                      tabIndex={0}

                      onKeyPress={(e) => {

                        if (e.key === 'Enter' || e.key === ' ') {

                          setActiveWellbeingCard(index);

                        }

                      }}

                    >

                      <div className="wellbeing-support-icon">

                        <i className={`fas ${support.icon}`} />

                      </div>

                      <h5>{support.title}</h5>

                      <p>{support.detail}</p>

                    </div>

                  ))}

                </div>

              </div>

            </div>

          </div>

        </div>

      </section>



      <section className="character-shaping-section" id="character-shaping">

        <div className="character-shaping-container">

          <div className="character-shaping-header life-animate">

            <p className="character-shaping-kicker">{characterShapingSection?.kicker || 'Our Framework'}</p>

            <h3>{characterShapingSection?.title || 'Character Shaping Program'}</h3>

            <p>{characterShapingSection?.description || 'A holistic approach to student development built on three interconnected pillars: Eastern Culture values as our foundation, Social Emotional Learning as our growth structure, and Wellbeing as our flourishing leaves.'}</p>

          </div>

          <div className="tree-interactive-wrapper life-animate">

            {/* Main Tree Image with Interactive Zones */}

            <div className="tree-image-container">

              <img 

                src={treeImage} 

                alt="Character Shaping Program: Eastern Culture Foundation, SEL Growth, Wellbeing Flourishing" 

                className="tree-image-main" 

                role="presentation"

              />

              {/* Eastern Culture - Bottom Center */}

              <button

                type="button"

                className="tree-interactive-label tree-label-roots"

                onClick={() => {

                  setActiveTreeZone('easternCulture');

                  setShowTreeModal(true);

                }}

                aria-label="Explore Eastern Culture"

              >

                EASTERN CULTURE

              </button>

              {/* SEL - Center Trunk */}

              <button

                type="button"

                className="tree-interactive-label tree-label-trunk"

                onClick={() => {

                  setActiveTreeZone('sel');

                  setShowTreeModal(true);

                }}

                aria-label="Explore Social Emotional Learning"

              >

                SOCIAL EMOTIONAL<br/>LEARNING

              </button>

              {/* Wellbeing - Right Top */}

              <button

                type="button"

                className="tree-interactive-label tree-label-leaves"

                onClick={() => {

                  setActiveTreeZone('wellbeing');

                  setShowTreeModal(true);

                }}

                aria-label="Explore Wellbeing"

              >

                WELLBEING

              </button>

            </div>

            {/* Remove old tree-buttons-overlay */}



            {/* Tree Zone Info Panels - Mobile/Below */}

            <div className="tree-zones-info-grid">

              <div

                className="tree-zone-info-card tree-zone-info-roots life-animate"

              >

                <div className="info-card-icon">

                  <i className="fas fa-om" />

                </div>

                <h4>Eastern Culture</h4>

                <p>The ROOT: Respect, harmony, and shared responsibility</p>

              </div>



              <div

                className="tree-zone-info-card tree-zone-info-trunk life-animate"

              >

                <div className="info-card-icon">

                  <i className="fas fa-brain" />

                </div>

                <h4>Social Emotional Learning</h4>

                <p>The TRUNK: Essential soft skills and emotional resilience</p>

              </div>



              <div

                className="tree-zone-info-card tree-zone-info-leaves life-animate"

              >

                <div className="info-card-icon">

                  <i className="fas fa-heart" />

                </div>

                <h4>Wellbeing</h4>

                <p>The LEAVES: Flourishing emotional and academic growth</p>

              </div>

            </div>

          </div>

        </div>

      </section>



      {showTreeModal && (

        <div className="tree-modal-overlay" onClick={() => setShowTreeModal(false)} role="presentation" aria-hidden="true">

          <div 

            className="tree-modal"

            onClick={(e) => e.stopPropagation()} 

            data-zone={activeTreeZone === 'easternCulture' ? 'eastern-culture' : activeTreeZone}

            role="dialog"

            aria-modal="true"

            aria-labelledby="tree-modal-title"

          >

            <button

              type="button"

              className="tree-modal-close"

              onClick={() => setShowTreeModal(false)}

              aria-label="Close Character Shaping modal"

            >

              <i className="fas fa-times" />

            </button>



            <div className="tree-modal-header">

              <div className="tree-modal-icon">

                {activeTreeZone === 'wellbeing' && <i className="fas fa-heart" />}

                {activeTreeZone === 'sel' && <i className="fas fa-brain" />}

                {activeTreeZone === 'easternCulture' && <i className="fas fa-leaf" />}

              </div>

              <h3 id="tree-modal-title">{treeZoneContent[activeTreeZone].title}</h3>

              <p className="tree-modal-subtitle">{treeZoneContent[activeTreeZone].subtitle}</p>

            </div>



            <div className="tree-modal-body">

              <div className="tree-zone-content-list">

                {treeZoneContent[activeTreeZone].content.map((section, idx) => (

                  <div key={idx} className="content-section">

                    <h4>{section.heading}</h4>

                    <ul>

                      {section.points.map((point, pIdx) => (

                        <li key={pIdx}>

                          <span className="list-bullet">•</span>

                          {point}

                        </li>

                      ))}

                    </ul>

                  </div>

                ))}

              </div>

            </div>



            <div className="tree-modal-footer">

              <p className="tree-modal-hint">Explore other zones to see more about our Character Shaping Program</p>

            </div>

          </div>

        </div>

      )}



      <section className="leaps-section" id="leaps">

        <div className="leaps-container">

          <div className="leaps-header">

            <p className="leaps-kicker">Student Development</p>

            <h3>LEAPS Framework</h3>

            <p>Our holistic approach to student growth encompasses five key dimensions that shape well-rounded learners and responsible global citizens.</p>

          </div>



          <div className="leaps-grid life-animate">

            {[

              {

                letter: 'L',

                fullText: 'Leadership',

                description: 'Developing confident decision-makers who inspire and guide others with integrity and vision.',

                icon: 'fa-crown',

                color: '#FF6B6B',

              },

              {

                letter: 'E',

                fullText: 'Extracurricular',

                description: 'Exploring passions through clubs, sports, arts, and activities that build skills beyond the classroom.',

                icon: 'fa-star',

                color: '#FFD93D',

              },

              {

                letter: 'A',

                fullText: 'Achievement',

                description: 'Pursuing academic excellence and personal growth through dedication, effort, and continuous improvement.',

                icon: 'fa-trophy',

                color: '#6BCB77',

              },

              {

                letter: 'P',

                fullText: 'Participation',

                description: 'Actively engaging in school life, community events, and collaborative projects that strengthen bonds.',

                icon: 'fa-handshake',

                color: '#4D96FF',

              },

              {

                letter: 'S',

                fullText: 'Service',

                description: 'Contributing to society through meaningful service, empathy, and commitment to making a positive impact.',

                icon: 'fa-heart',

                color: '#1E3A8A',

              },

            ].map((item, index) => (

              <div

                key={item.letter}

                className={`leaps-card life-animate ${flippedLeapsCard === item.letter ? 'flipped' : ''}`}

                style={{ '--card-color': item.color }}

                onClick={() => setFlippedLeapsCard(flippedLeapsCard === item.letter ? null : item.letter)}

                role="button"

                tabIndex={0}

                onKeyPress={(e) => {

                  if (e.key === 'Enter' || e.key === ' ') {

                    setFlippedLeapsCard(flippedLeapsCard === item.letter ? null : item.letter);

                  }

                }}

              >

                <div className="leaps-card-front">

                  <div className="leaps-letter" style={{ backgroundColor: item.color }}>

                    {item.letter}

                  </div>

                </div>

                <div className="leaps-card-back">

                  <div className="leaps-back-content">

                    <h4>{item.fullText}</h4>

                    <p>{item.description}</p>

                    <div className="leaps-icon">

                      <i className={`fas ${item.icon}`} />

                    </div>

                  </div>

                </div>

              </div>

            ))}

          </div>

        </div>

      </section>

    </section>

  );

}



export default StudentLifePage;

